import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '../i18n';
import { corporateContent, companyDetails, pressContacts } from '../content/corporateContent';
import { ReactComponent as AidagisQr } from '../assets/qr-aidagis.svg';
import './CorporateSections.css';

export const CompanyLegal = ({ scope = 'contact' }) => <div className="company-legal" data-testid={`${scope}-company-details`}>
  <p className="company-full-name" data-testid={`${scope}-company-name`}>{companyDetails.name}</p>
</div>;

export const CompanyLinks = ({ scope = 'footer' }) => {
  const { language } = useI18n();
  const copy = corporateContent[language];
  return <div className="company-links">
    <section><h3 className="corporate-label" data-testid={`${scope}-email-label`}>{copy.emailLabel}</h3><a data-testid={`${scope}-email-link`} href={`mailto:${companyDetails.email}`}>{companyDetails.email}</a>
      <h3 className="corporate-label company-phone-heading" data-testid={`${scope}-phone-label`}>{copy.phoneLabel}</h3>{companyDetails.phones.map((phone, index) => <a key={phone.href} href={phone.href} data-testid={`${scope}-phone-link-${index + 1}`}>{phone.display}</a>)}
    </section>
    <section><h3 className="corporate-label" data-testid={`${scope}-telegram-label`}>Telegram</h3>{companyDetails.telegram.map((telegram, index) => <a key={telegram.href} href={telegram.href} target="_blank" rel="noopener noreferrer" data-testid={`${scope}-telegram-link-${index + 1}`}>{telegram.display}<ArrowUpRight aria-hidden="true" /></a>)}</section>
  </div>;
};

export const WhatWeCreate = ({ onContact }) => {
  const { language } = useI18n();
  const copy = corporateContent[language];
  return <section id="capabilities" className="visual-section corporate-theme create-section" aria-labelledby="create-heading" data-testid="section-what-we-create">
    <h2 id="create-heading" className="create-heading" data-testid="what-we-create-title">{copy.createTitle}</h2>
    <ol className="create-grid" data-testid="what-we-create-grid">{copy.capabilities.map((item, index) => <li data-testid={`what-we-create-item-${index + 1}`} key={index}>
      <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
      <div className="create-item-copy">
        <h3 data-testid={`what-we-create-item-${index + 1}-title`}>{item}</h3>
        <p className="create-description" data-testid={`what-we-create-item-${index + 1}-description`}>{copy.capabilityDescriptions[index]}</p>
      </div>
    </li>)}</ol>
    <div className="create-contact-row">
      <Button type="button" variant="outline" className="create-contact-cta" onClick={event => onContact(event.currentTarget)} aria-haspopup="dialog" data-testid="what-we-create-contact-cta">{copy.contactCta}<ArrowUpRight aria-hidden="true"/></Button>
    </div>
  </section>;
};

export const PeopleProfile = ({ person, portrait, portraitWidth, portraitHeight = 1280, portraitSize, index, feedback }) => {
  const prefix = index === 0 ? 'people-profile' : `people-profile-${index + 1}`;
  const [portraitState, setPortraitState] = useState('loading');
  const [portraitAttempt, setPortraitAttempt] = useState(0);
  const portraitSrc = portrait ? `${process.env.PUBLIC_URL || ''}/team/${portrait}.webp${portraitAttempt ? `?retry=${portraitAttempt}` : ''}` : null;
  const retryPortrait = () => { setPortraitState('loading'); setPortraitAttempt(value => value + 1); };
  return <article className={`people-profile${portraitSize === 'large' ? ' people-profile-featured' : ''}${person.showContacts ? ' people-profile-press' : ''}`} aria-labelledby={`${prefix}-name`} data-testid={`people-profile-${index + 1}`}>
    <Card className="people-portrait-card" aria-busy={Boolean(portrait) && portraitState === 'loading'} data-testid={index === 0 ? 'people-portrait-card' : `${prefix}-portrait-card`}>
      <AspectRatio ratio={person.showContacts ? portraitWidth / portraitHeight : 1}>
        {!portrait ? <div className="people-empty-portrait" data-testid={`${prefix}-portrait-empty`}><span className="sr-only">{person.portraitMissing}</span></div> : <>
        {portraitState === 'loading' && <Skeleton className="people-portrait-skeleton" aria-hidden="true"/>}
        <img className="people-portrait" data-loaded={portraitState === 'loaded'} data-testid={`${prefix}-portrait`} src={portraitSrc} alt={person.portraitAlt} width={portraitWidth} height={portraitHeight} loading="lazy" decoding="async" hidden={portraitState === 'error'} onLoad={() => setPortraitState('loaded')} onError={() => setPortraitState('error')}/>
        {portraitState !== 'loaded' && <div className="people-portrait-status" role={portraitState === 'error' ? 'alert' : 'status'} data-testid={`${prefix}-portrait-status`}>
          <p data-testid={`${prefix}-portrait-status-text`}>{portraitState === 'error' ? feedback.portraitError : feedback.portraitLoading}</p>
          {portraitState === 'error' && <Button type="button" variant="outline" className="people-portrait-retry" onClick={retryPortrait} data-testid={`${prefix}-portrait-retry`}>{feedback.portraitRetry}</Button>}
        </div>}
        </>}
      </AspectRatio>
    </Card>
    <div className="people-bio">
      <h3 id={`${prefix}-name`} className="people-name" data-testid={`${prefix}-name`}>{person.name}</h3>
      {person.role && <h4 className="people-role" data-testid={`${prefix}-role`}>{person.role}</h4>}
      {(person.degrees || person.honor) && <div className="people-credentials">
        {person.degrees && <p data-testid={`${prefix}-degrees`}>{person.degrees}</p>}
        {person.honor && <p data-testid={`${prefix}-honor`}>{person.honor}</p>}
      </div>}
      {person.education && <div className="people-education">{person.education.map((text, position) => <p key={position} data-testid={`${prefix}-education-${position + 1}`}>{text}</p>)}</div>}
      {person.summary && <p className="people-summary" data-testid={`${prefix}-summary`}>{person.summary}</p>}
      {person.specialties && <p className="people-specialties" aria-label={person.specialtiesTitle} data-testid={`${prefix}-specialties`}>{person.specialties.map((specialty, position) => <span key={position} data-testid={`${prefix}-specialty-${position + 1}`}>{specialty}{position < person.specialties.length - 1 ? '; ' : '.'}</span>)}</p>}
      {person.aiExpertise && <p className="people-ai-expertise" data-testid={`${prefix}-ai-expertise`}>{person.aiExpertise}</p>}
      {person.showContacts && <div className="people-contacts" data-testid={`${prefix}-contacts`}>
        <a data-testid={`${prefix}-contact-email`} href={`mailto:${companyDetails.email}`}>{companyDetails.email}</a>
        <a data-testid={`${prefix}-contact-telegram-1`} href={pressContacts.telegram.href} target="_blank" rel="noopener noreferrer">{pressContacts.telegram.display}<ArrowUpRight aria-hidden="true"/></a>
      </div>}
    </div>
  </article>;
};

export const PeopleSection = () => {
  const { language } = useI18n();
  const copy = corporateContent[language];
  const profiles = [
    { portrait: 'roza-salieva', portraitWidth: 853, person: copy.people },
    { portrait: 'timur-fttakhov-close', portraitWidth: 500, portraitHeight: 500, portraitSize: 'large', person: copy.peopleCEO },
    { portrait: 'simushkin', portraitWidth: 640, portraitHeight: 640, person: copy.peopleEngineering },
    { portrait: 'press', portraitWidth: 960, portraitHeight: 1280, person: copy.peoplePress },
  ];
  return <section id="studies" className="visual-section corporate-theme people-section" aria-labelledby="people-heading" data-testid="people-section">
    <div className="section-index"><span data-testid="people-section-index">05</span><span className="index-rule" aria-hidden="true"/></div>
    <h2 id="people-heading" className="people-heading" data-testid="people-section-title">{copy.people.title}</h2>
    <div className="people-profiles-grid" data-testid="people-profiles-grid">{profiles.map((profile, index) => <PeopleProfile key={profile.portrait} {...profile} index={index} feedback={copy.people}/>)}</div>
  </section>;
};

export const FooterQRCode = () => {
  const { language } = useI18n();
  const copy = corporateContent[language];
  return <Button asChild variant="ghost" className="footer-qr" data-testid="footer-qr-link">
    <a href={companyDetails.website} target="_blank" rel="noopener noreferrer" aria-label={copy.qrAlt}>
      <AidagisQr className="footer-qr-image" role="img" aria-label={copy.qrAlt} data-testid="footer-qr-image" focusable="false"/>
    </a>
  </Button>;
};

export const CorporateFooter = ({ wordmark, logo, onContact }) => {
  const { language } = useI18n();
  const copy = corporateContent[language];
  return <footer className="footer corporate-footer corporate-theme" id="footer" data-testid="corporate-footer">
    <div className="footer-bar"><span aria-hidden="true">06</span><Button variant="ghost" className="footer-contact-button" onClick={onContact} data-testid="footer-contact-cta">{copy.contactCta}<ArrowUpRight aria-hidden="true" /></Button></div>
    {wordmark}
    <div className="footer-qr-row" data-testid="footer-qr-row"><CompanyLegal scope="footer"/><FooterQRCode/></div>
    <div className="footer-legal-block" data-testid="footer-legal-block"><CompanyLinks scope="footer"/></div>
    <div className="footer-copyright-row">{logo}<p data-testid="footer-copyright">{copy.rights}</p></div>
  </footer>;
};
